import React from "react";
import PageContainerWithLogo from '../Core/PageContainerWithLogo';

export default function PrivacyPolicy()
{
  return (
    <PageContainerWithLogo>

      <div className="privacy-page">
        <div className="privacy-container">
          <h1>Privacy Policy</h1>
          <p className="updated">Last updated: December 2025</p>

          <p>
            This Privacy Policy explains how we collect, use, and protect your
            information when you use <strong>Tilefolk.io</strong> (the
            "Service", "we", "us", or "our"). By using the Service, you agree to
            this Privacy Policy.
          </p>

          <h2>1. Information We Collect</h2>
          <h3>A. Account Information</h3>
          <p>
            If you sign in using social login services (currently Google Sign-In,
            with other providers potentially added in the future), we receive your
            email address and profile name (if shared by the provider). This
            information is used to create and manage your game account, associate
            your progress with your devices, and verify your identity when you
            sign in from different devices.
          </p>

          <h3>B. Cookies and Session Data</h3>
          <p>
            We use cookies and similar technologies to keep you logged in,
            remember your game progress, and securely identify your device. We
            also use third-party analytics and (in the future) advertising cookies,
            described below.
          </p>

          <h3>C. Server-Side Game Analytics</h3>
          <p>
            We automatically collect anonymous gameplay data on our servers to monitor
            game performance, track feature usage, and improve the gaming experience.
            This includes information such as game actions, session duration, feature
            interactions, and technical performance metrics. <strong>No personally
              identifiable information is included in these analytics</strong> - they are
            completely anonymous and essential for maintaining and improving our service.
            These analytics continue to operate even if you opt out of optional
            client-side tracking.
          </p>

          <h3>D. Client-Side Analytics Data (Amplitude)</h3>
          <p>
            We use <strong>Amplitude</strong> for optional enhanced analytics to
            understand how players use our game — for example, which features are
            used most often or how long sessions last. Amplitude may collect
            information such as your device type, browser, operating system, IP
            address (which may be anonymized), and gameplay events. You can learn
            more at{ " " }
            <a
              href="https://amplitude.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Amplitude's Privacy Policy
            </a>
            . In the EU/UK, Amplitude cookies will only run after you give consent
            through our cookie banner.
          </p>

          <h3>E. Error Monitoring and Crash Analytics (Sentry)</h3>
          <p>
            We use <strong>Sentry</strong> to monitor application errors and crashes
            to improve the stability and performance of our service. Sentry may
            collect technical information such as error messages, stack traces,
            device information, browser details, and IP addresses (which may be
            anonymized). This helps us identify and fix bugs quickly. You can learn
            more at{ " " }
            <a
              href="https://sentry.io/privacy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sentry's Privacy Policy
            </a>
            .
          </p>

          <h3>F. Automatically Collected Data</h3>
          <p>
            Our servers may automatically collect browser type and version, IP
            address, access times, and gameplay data for debugging and security.
          </p>

          <h2>2. How We Use Your Information</h2>
          <ul>
            <li>Provide and improve the game experience</li>
            <li>Save your progress and preferences</li>
            <li>Manage your user account</li>
            <li>Track anonymous gameplay metrics for service optimization</li>
            <li>Analyze usage through optional client-side analytics (Amplitude)</li>
            <li>Monitor and fix technical issues through Sentry</li>
            <li>Respond to feedback and maintain site security</li>
          </ul>
          <p>We do <strong>not</strong> sell or share your information for marketing purposes.</p>

          <h2>3. Use of Google Services</h2>
          <p>
            If you sign in with Google, Google processes your login according to{ " " }
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google's Privacy Policy
            </a>
            . When we add Google AdSense or other advertising services, ad cookies
            will only load after you give consent. Learn more at{ " " }
            <a
              href="https://policies.google.com/technologies/partner-sites"
              target="_blank"
              rel="noopener noreferrer"
            >
              How Google uses information from partner sites
            </a>
            .
          </p>

          <h2>4. Legal Basis for Processing (GDPR)</h2>
          <ul>
            <li><strong>Consent</strong> – for optional client-side analytics or advertising cookies</li>
            <li><strong>Contract</strong> – to provide gameplay access</li>
            <li><strong>Legitimate Interest</strong> – to secure and improve the Service, including anonymous server-side game analytics necessary for service operation</li>
          </ul>

          <h2>5. Data Retention</h2>
          <p>
            We keep your data as long as necessary to maintain your account and
            comply with legal obligations. You may request deletion at any time.
          </p>

          <h2>6. Your Rights</h2>
          <ul>
            <li>Access or delete your personal data</li>
            <li>Correct inaccuracies</li>
            <li>Withdraw consent at any time</li>
            <li>Request a copy of your data</li>
          </ul>
          <p>
            To exercise your rights, contact us at{ " " }
            <a href="mailto:help@tilefolk.io">help@tilefolk.io</a>.
          </p>

          <h2>7. Data Security</h2>
          <p>
            We use HTTPS and secure servers to protect your data, but no online
            system is completely secure.
          </p>

          <h2>8. Data Collection and Consent</h2>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Purpose</th>
                <th>Consent Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Essential Cookies</td>
                <td>Login, session, security</td>
                <td>No</td>
              </tr>
              <tr>
                <td>Server-Side Game Analytics</td>
                <td>Anonymous gameplay tracking for service optimization</td>
                <td>No (Essential for service)</td>
              </tr>
              <tr>
                <td>Client-Side Analytics (Amplitude)</td>
                <td>Optional enhanced usage tracking and insights</td>
                <td>Yes (EU/UK)</td>
              </tr>
              <tr>
                <td>Error Monitoring (Sentry)</td>
                <td>Crash analytics and debugging</td>
                <td>No (Legitimate Interest)</td>
              </tr>
              <tr>
                <td>Advertising (Future)</td>
                <td>Personalized or non-personalized ads</td>
                <td>Yes (EU/UK)</td>
              </tr>
            </tbody>
          </table>

          <h2>9. Your Privacy Rights & Consent Management</h2>
          <h3>A. Consent</h3>
          <p>
            Where required by law (such as under GDPR), we obtain your explicit consent
            before collecting optional client-side analytics data or setting non-essential cookies. You can:
          </p>
          <ul>
            <li>Withdraw your consent for optional analytics at any time through your account settings</li>
            <li>Choose which types of optional data collection you're comfortable with</li>
            <li>Request deletion of your optional analytics data</li>
          </ul>
          <p>
            <strong>Note:</strong> Anonymous server-side game analytics are essential for
            service operation and continue regardless of consent preferences, as they contain
            no personally identifiable information and are necessary for maintaining game
            functionality and performance.
          </p>

          <h3>B. Your Rights (GDPR & Other Privacy Laws)</h3>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Correct inaccurate personal data</li>
            <li><strong>Erasure:</strong> Request deletion of your personal data</li>
            <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
            <li><strong>Object:</strong> Object to processing of your personal data</li>
            <li><strong>Restrict:</strong> Restrict how we process your personal data</li>
          </ul>
          <p>
            To exercise these rights, please contact us at{ ' ' }
            <a href="mailto:help@tilefolk.io">help@tilefolk.io</a>.
          </p>

          <h2>10. Children's Privacy</h2>
          <p>
            We do not knowingly collect personal data from children under 13 (or 16
            in some regions). Please contact us if you believe this has occurred.
          </p>

          <h2>11. Updates</h2>
          <p>
            We may update this Privacy Policy from time to time. The most recent
            version will always appear here with a revised "Last updated" date.
          </p>

          <h2>12. Contact Us</h2>
          <p>
            If you have questions or requests about this Privacy Policy, please
            contact us at <a href="mailto:help@tilefolk.io">help@tilefolk.io</a> or via
            our Contact page.
          </p>
        </div>
      </div>
    </PageContainerWithLogo>
  );
}
